import { Injectable, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import DOMPurify from 'dompurify';

/**
 * Centralised HTML sanitization for content that must be rendered via
 * `[innerHTML]` or `bypassSecurityTrustHtml`. All user-authored and
 * AI-generated HTML MUST flow through this service before reaching the DOM.
 *
 * Uses DOMPurify. Angular's DomSanitizer.bypassSecurityTrustHtml is called
 * only after DOMPurify has produced a clean string.
 */
@Injectable({ providedIn: 'root' })
export class HtmlSanitizerService {
  private readonly sanitizer = inject(DomSanitizer);

  /**
   * Returns a SafeHtml value suitable for [innerHTML].
   * Strips <script>, event handlers, javascript: URIs, and other XSS vectors.
   */
  sanitizeTrusted(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.sanitizeToString(html));
  }

  /**
   * Returns a sanitized HTML string (no SafeHtml wrapper). Use when you
   * need to concatenate or post-process the HTML before display.
   */
  sanitizeToString(html: string): string {
    if (!html) return '';
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'a', 'b', 'br', 'code', 'div', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'hr', 'i', 'img', 'li', 'ol', 'p', 'pre', 'span', 'strong', 'sub', 'sup',
        'table', 'tbody', 'td', 'th', 'thead', 'tr', 'u', 'ul'
      ],
      ALLOWED_ATTR: [
        'href', 'src', 'alt', 'title', 'class', 'style',
        'data-term-id', 'data-type', 'data-id',
        'target', 'rel', 'colspan', 'rowspan'
      ],
      ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|data:image\/(png|jpe?g|gif|webp|svg\+xml);base64,|#)/i,
      FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur']
    });
  }

  /**
   * Sanitizes a full HTML document (with <html>, <head>, <style>). Used for
   * PDF generation and iframe preview where inline styles are required.
   * Strips <script>, event handlers, and dangerous URIs but keeps <style>
   * and the document shell.
   */
  sanitizeDocument(html: string): string {
    if (!html) return '';
    return DOMPurify.sanitize(html, {
      WHOLE_DOCUMENT: true,
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onsubmit'],
      ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|data:image\/(png|jpe?g|gif|webp|svg\+xml);base64,|#)/i
    });
  }

  /**
   * Escapes a plain-text string so it can be safely injected as text content.
   * Use for concatenation where you want to guarantee no HTML interpretation.
   */
  escapeText(s: string): string {
    if (!s) return '';
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
